import type { ReactNode } from "react";
import { ActivityIndicator, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
// @ts-expect-error baseline 2026-02-16
import { ReportsSection, ReportsListItem, ReportsListItemText } from "@src/components/web";
import { isWeb } from "@src/utils/web-helpers";
import { useReportEntitlements } from "@src/features/reports/hooks/useReportEntitlements";
import type { ReportEntitlements } from "@src/features/reports/entitlements";
import type { ReportType } from "@visioni/a3s-reports/client-messages";

/**
 * How each report type presents as a tile. Keyed by `ReportType` rather than
 * listed, so a report type added in a3s-reports fails to compile until it says
 * how it introduces itself here.
 *
 * The blurbs stay JSX so their typographic quotes and dashes can be written as
 * HTML entities, exactly as they were before the tiles became data.
 */
const TILES: Record<ReportType, { readonly title: string; readonly blurb: ReactNode }> = {
  wastesense: {
    title: "WasteSense",
    blurb: (
      <>
        <ReportsListItemText>A digest of illegal dumping and site activity.</ReportsListItemText>
        <ReportsListItemText>
          Provides a clear answer to: &ldquo;Did anything happen on our site?&rdquo;
        </ReportsListItemText>
      </>
    ),
  },
  roadsense: {
    title: "RoadSense",
    blurb: (
      <>
        <ReportsListItemText>A clear picture of vehicle flow and traffic insights.</ReportsListItemText>
        <ReportsListItemText>Know exactly who and what moved through your site &mdash; and when.</ReportsListItemText>
      </>
    ),
  },
};

export default function ReportsScreen() {
  const { tenantId } = useLocalSearchParams<{ tenantId: string }>();
  const entitlements = useReportEntitlements();

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          Reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this page in your browser to view your reports.
        </Text>
      </View>
    );
  }

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingTop: 40,
        paddingBottom: 40,
        paddingHorizontal: 80,
        maxWidth: sizes.webContentMaxWidth,
      }}
      showsVerticalScrollIndicator={false}
    >
      <View gap="6xl">
        <ReportsSection title="Reports">
          <ReportTiles tenantId={tenantId} entitlements={entitlements} />
        </ReportsSection>
      </View>
    </ScrollView>
  );
}

interface ReportTilesProps {
  readonly tenantId: string;
  readonly entitlements: ReportEntitlements;
}

/**
 * The section body: one tile per report the tenant's hires entitle it to.
 *
 * Loading, error and empty are rendered inside the section rather than
 * collapsing it, so the chrome stays put across refetches - the same shape the
 * child report screens use for their own list bodies.
 *
 * Nothing is rendered optimistically. While the answer is unknown this shows a
 * spinner, never a tile it might have to take away.
 */
function ReportTiles({ tenantId, entitlements }: ReportTilesProps) {
  if (entitlements.status === "pending") {
    return (
      <View padding="3xl" alignItems="center" gap="md">
        <ActivityIndicator />
        <Text textStyle="xs/regular" color="textMuted">
          Loading your reports...
        </Text>
      </View>
    );
  }

  if (entitlements.status === "error") {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          Couldn&apos;t load your reports
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          {entitlements.message || "An unexpected error occurred."}
        </Text>
      </View>
    );
  }

  if (entitlements.reportTypes.length === 0) {
    return (
      <View padding="3xl" alignItems="center" gap="sm">
        <Text textStyle="sm/semibold" color="text">
          No reports for this account
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Reports appear here when a site is hired for a service that produces one.
        </Text>
      </View>
    );
  }

  const base = `/tenants/${tenantId}/reports`;

  return (
    <>
      {entitlements.reportTypes.map((type) => (
        <ReportsListItem key={type} title={TILES[type].title} href={`${base}/${type}`}>
          {TILES[type].blurb}
        </ReportsListItem>
      ))}
    </>
  );
}
